import React, { memo, type ComponentType } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { Skeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from '../common/constants.tsx';
import { useFeatures } from '../controllers/features/index.tsx';
import type { GlobalProductsSettingsSidebarQuery } from './__generated__/GlobalProductsSettingsSidebarQuery.graphql';
import IntegrationsSection from './integrations-section/index.tsx';
import JiraSection from './jira-section/index.tsx';
import OperationsSection from './operations-section/index.tsx';
import PlansSection from './plans-section/index.tsx';
import ProductDiscoverySection from './product-discovery-section/index.tsx';
import { ServiceManagementSection } from './service-management-section/ServiceManagementSection.tsx';
import CustomerServiceSection from './customer-service-section/index.tsx';
import { DelegatedAdminJiraSection } from './jira-section/DelegatedAdminJiraSection.tsx';
import ServiceCollectionSection from './service-collection-section/ServiceCollectionSection.tsx';

const productAccessItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-access-item',
	defaultMessage: 'Product access',
	description: 'Navigation item which links to a product access settings page',
});

const productAccessItemAppifyMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-access-item-appify',
	defaultMessage: 'App access',
	description: 'Navigation item which links to a product access settings page',
});

type Props = {
	queryReference: PreloadedQuery<GlobalProductsSettingsSidebarQuery>;
};

const GlobalProductsSettingsSidebarInner = ({ queryReference }: Props) => {
	const productAccessItemAppify = fg('product-terminology-refresh')
		? productAccessItemAppifyMessage
		: productAccessItem;
	const { formatMessage } = useIntl();

	const queryData = usePreloadedQuery<GlobalProductsSettingsSidebarQuery>(
		graphql`
			query GlobalProductsSettingsSidebarQuery(
				$cloudId: ID!
				$skipDelegatedAdminCheck: Boolean!
				$skipSiteWfoStatus: Boolean!
			) @preloadable {
				...DelegatedAdminJiraSection
					@arguments(cloudId: $cloudId, skipDelegatedAdminCheck: $skipDelegatedAdminCheck)
				...ServiceManagementSection_queryNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts
					@arguments(cloudId: $cloudId, skipSiteWfoStatus: $skipSiteWfoStatus)
			}
		`,
		queryReference,
	);
	const { data: features } = useFeatures();

	if (!features) {
		return (
			<>
				<Skeleton />
				<UFOLoadHold name="products-data-loading" />
			</>
		);
	}

	if (features.hasJsmAppAdminOnlyAccess && features.hasServiceManagementSection) {
		return (
			<>
				{fg('csm_appification_jira_admin') && <ServiceCollectionSection features={features} />}
				<ServiceManagementSection features={features} />
			</>
		);
	}

	return (
		<>
			{features.hasApplicationAccess && (
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'productAccess' }}
					href="/secure/admin/ApplicationAccess.jspa"
					target="_blank"
					elemAfter={<LinkExternalIcon spacing="none" label="" color={token('color.icon')} />}
				>
					{formatMessage(productAccessItemAppify)}
				</LinkMenuItem>
			)}
			{features.hasSoftwareSection && features.hasSoftwareConfig && <JiraSection />}
			{/*
			Only show the DelegatedAdminJiraSection if the JiraSection isn't already being shown.
			*/}
			{!(features.hasSoftwareSection && features.hasSoftwareConfig) && (
				<DelegatedAdminJiraSection queryReference={queryData} />
			)}
			{features.hasPlansSection && <PlansSection />}
			{features.hasServiceManagementSection && (
				<>
					{fg('csm_appification_jira_admin') && <ServiceCollectionSection features={features} />}
					<ServiceManagementSection features={features} queryFragment={queryData} />
				</>
			)}
			{features.hasCustomerServiceSection && <CustomerServiceSection />}
			{features.hasOperationsSection && <OperationsSection features={features} />}
			{features.hasProductDiscoverySection && <ProductDiscoverySection />}
			{features.hasIntegrationsSection && <IntegrationsSection features={features} />}
		</>
	);
};

export const GlobalProductsSettingsSidebar: ComponentType<Props> = memo(
	GlobalProductsSettingsSidebarInner,
);
